import {
    createProperties,
    createRoot,
    type Field,
    Form,
    type FormState,
    type TypeToFields,
} from '@jmvdevelop/svelte-form';
import findIndex from 'lodash/findIndex';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { match } from 'ts-pattern';
import * as z from 'zod';
import type { UrlHelper } from '$lib/UrlHelper';
import type { SvelteClient } from '$lib/svelteClient/SvelteClient';
import { delay } from '$lib/utils/delay';
import { mergeObject } from '$lib/utils/mergeObject';
import { zSearchHeaderFormData_TypeEnum } from '$gql/zod-generated';
import * as JobFiltersData from '$components/SearchHeader/JobFilters/JobFiltersData';
import * as NewsfeedFiltersData from '$components/SearchHeader/NewsfeedFilters/NewsfeedFiltersData';
import * as ProFiltersData from '$components/SearchHeader/ProFilters/ProFiltersData';

const zWhatWhoItem = z.object({
    userId: z.number().nullish(),
    categoryId: z.number().nullish(),
    companyId: z.number().nullish(),
    shoppingProductId: z.number().nullish(),
    jobOfferId: z.number().nullish(),
    text: z.string().nullish(),
});

export type WhatWhoItem = z.infer<typeof zWhatWhoItem>;

const zWhereItem = z.object({
    cityId: z.number().nullish(),
    subCityId: z.number().nullish(),
    districtId: z.number().nullish(),
    countryId: z.string().nullish(),
});

export type WhereItem = z.infer<typeof zWhereItem>;

export const zSearchHeaderFormData = z.object({
    type: zSearchHeaderFormData_TypeEnum,
    whatWhoQuery: z.string().nullable(),
    whatWhoItem: zWhatWhoItem.nullable(),
    whereQuery: z.string().nullable(),
    whereItem: zWhereItem.nullable(),
    hashtag: z.string().nullable(),
    showFilters: z.boolean(),
    proFilters: ProFiltersData.zProsFiltersData,
    jobFilters: JobFiltersData.zJobFiltersData,
    newsfeedFilters: NewsfeedFiltersData.zNewsfeedFiltersData,
});

export type SearchHeaderFormData = z.infer<typeof zSearchHeaderFormData>;
type FormData = SearchHeaderFormData;

export type Fields<TFormValues> = TypeToFields<
    TFormValues,
    FormData,
    keyof FormData,
    {
        proFilters: ProFiltersData.Fields<TFormValues>;
        newsfeedFilters: NewsfeedFiltersData.Fields<TFormValues>;
        jobFilters: JobFiltersData.Fields<TFormValues>;
    }
>;

export const empty: FormData = {
    type: 'company',
    whatWhoQuery: null,
    whatWhoItem: null,
    whereQuery: null,
    whereItem: null,
    hashtag: null,
    showFilters: false,
    proFilters: ProFiltersData.empty,
    newsfeedFilters: NewsfeedFiltersData.empty,
    jobFilters: JobFiltersData.empty,
};

export function createFields<TValues>(self: Field<TValues, FormData>): Fields<TValues> {
    const properties = createProperties(self, [
        'type',
        'whatWhoQuery',
        'whatWhoItem',
        'whereQuery',
        'whereItem',
        'hashtag',
        'showFilters',
        'proFilters',
        'newsfeedFilters',
        'jobFilters',
    ] as const);

    return {
        self: self,
        ...properties,
        proFilters: ProFiltersData.createFields(properties.proFilters),
        newsfeedFilters: NewsfeedFiltersData.createFields(properties.newsfeedFilters),
        jobFilters: JobFiltersData.createFields(properties.jobFilters),
    };
}

export function validate(
    _form: Form<FormData, string>,
    _fields: Fields<FormData>,
    state: FormState<FormData, string>,
) {
    return state;
}

export function createForm(
    id: string,
    initialValues: Partial<FormData>,
): [Form<FormData, string>, Fields<FormData>] {
    const fields = createFields<FormData>(createRoot<FormData>());
    const form = new Form<FormData, string>(
        { initialValues: mergeObject(empty, initialValues) },
        id,
    );

    form.addValidator((state) => {
        return validate(form, fields, state);
    });

    form.addOnChangeValues((changedFields, prevState, nextState) => {
        if (
            findIndex(changedFields, (f) => f.getKey() === fields.whereQuery.getKey()) !==
            -1
        ) {
            const prevWhereQuery = prevState.values.whereQuery;
            const nextWhereQuery = nextState.values.whereQuery;
            if (
                prevWhereQuery !== nextWhereQuery &&
                nextState.values.whereItem !== null
            ) {
                nextState = form.actions().changeValue(null, fields.whereItem)(nextState);
            }
        }

        if (
            findIndex(
                changedFields,
                (f) => f.getKey() === fields.whatWhoQuery.getKey(),
            ) !== -1
        ) {
            const prevWhatWhoQuery = prevState.values.whatWhoQuery;
            const nextWhatWhoQuery = nextState.values.whatWhoQuery;
            if (
                prevWhatWhoQuery !== nextWhatWhoQuery &&
                nextState.values.whatWhoItem !== null
            ) {
                nextState = form.actions().changeValue(null, fields.whatWhoItem)(
                    nextState,
                );
            }
        }

        return nextState;
    });

    return [form, fields];
}

export function gotoSearchUrl(
    values: SearchHeaderFormData,
    page: number,
    {
        urlHelper,
        svelteClient,
    }: {
        urlHelper: UrlHelper;
        svelteClient: SvelteClient;
    },
): Promise<void> {
    return match(values.type)
        .with('pro', 'newsfeed', () => {
            return svelteClient
                .goto(urlHelper.searchHeader({ searchForm: values, page }), {
                    noScroll: true,
                    replaceState: true,
                    keepFocus: true,
                })
                .catch(console.error);
        })
        .with('company', async () => {
            window.location.assign(urlHelper.searchHeader({ searchForm: values, page }));
            await delay(10000);
        })
        .with('job', async () => {
            return svelteClient
                .goto(urlHelper.searchHeader({ searchForm: values, page }), {
                    noScroll: true,
                    replaceState: true,
                    keepFocus: true,
                })
                .catch(console.error);
        })
        .exhaustive()
        .catch((e) => {
            console.error(e);
            throw e;
        });
}

export function isEqualWithoutLookingAtTheShowFilters(
    a: SearchHeaderFormData,
    b: SearchHeaderFormData,
): boolean {
    return isEqual(omit(a, ['showFilters']), omit(b, ['showFilters']));
}
